// Parent side of the embedded-app token handshake (VUE-9 → Handshake). The
// WebApp alone holds the refresh token; every embedded app gets only the access
// token through postMessage. Pure logic: embedded-app-token wires it to the
// window, the shared axios refresh and the auth EventBus events.

export const EMBEDDED_APP_MESSAGE = {
    COMPLETED_LOADING: "COMPLETED_LOADING",
    ACCESS_TOKEN_EXPIRED: "ACCESS_TOKEN_EXPIRED",
    ACCESS_TOKEN: "ACCESS_TOKEN",
    ACCESS_TOKEN_ERROR: "ACCESS_TOKEN_ERROR",
    AUTH_LOGOUT: "AUTH_LOGOUT"
} as const;

export type SignOutReason = "logout" | "session-expired";

export interface AccessTokenPayload {
    accessToken: string;
    accessTokenExpiration: string | null;
    webApiEndPoint: string;
    webApiVersion: string;
    languageId: number;
    isMasterUser: boolean;
}

export type TokenSnapshot = Omit<AccessTokenPayload, "languageId">;

export interface EmbeddedAppRegistration {
    // Exact origin: the only accepted event.origin and the postMessage targetOrigin.
    origin: string;
    // Read on every message and post: hosts re-create their iframes.
    getContentWindow: () => Window | null;
    getLanguageId: () => number;
    // Runs after the ACCESS_TOKEN answering COMPLETED_LOADING was posted.
    onCompletedLoading?: () => void;
}

export interface EmbeddedAppTokenBrokerDeps {
    // null = no access token stored.
    readToken: () => TokenSnapshot | null;
    // Shared refresh: resolves the new token, or null when it failed.
    refreshAccessToken: () => Promise<string | null>;
    now?: () => number;
}

// Children ask for a new token within this margin of expiry; the parent uses the
// same margin so such a request always gets a fresh token instead of the old one.
export const MIN_TOKEN_VALIDITY_MS = 60 * 1000;

type MessageLike = Pick<MessageEvent, "origin" | "source" | "data">;

interface Entry {
    registration: EmbeddedAppRegistration;
    // Has sent COMPLETED_LOADING or ACCESS_TOKEN_EXPIRED since it was registered.
    active: boolean;
    lastSentToken: string | null;
}

// Unknown or unparseable expiry: NaN, which every caller reads as "still valid".
const expiresAt = (token: TokenSnapshot) =>
    token.accessTokenExpiration ? Date.parse(token.accessTokenExpiration) : NaN;

export class EmbeddedAppTokenBroker {
    private entries: Entry[] = [];
    private readonly now: () => number;

    constructor(private readonly deps: EmbeddedAppTokenBrokerDeps) {
        this.now = deps.now || Date.now;
    }

    register(registration: EmbeddedAppRegistration): () => void {
        const entry: Entry = { registration, active: false, lastSentToken: null };
        this.entries.push(entry);
        return () => {
            this.entries = this.entries.filter(e => e !== entry);
        };
    }

    async handleMessage(event: MessageLike): Promise<void> {
        const entry = this.entries.find(e =>
            event.origin === e.registration.origin &&
            !!event.source &&
            event.source === e.registration.getContentWindow()
        );
        if (!entry) return;

        const data: unknown = event.data;
        if (!data || typeof data !== "object" || !("type" in data)) return;
        const type = data.type;
        if (type !== EMBEDDED_APP_MESSAGE.COMPLETED_LOADING && type !== EMBEDDED_APP_MESSAGE.ACCESS_TOKEN_EXPIRED) return;
        const isLoading = type === EMBEDDED_APP_MESSAGE.COMPLETED_LOADING;
        const payload = "payload" in data ? data.payload : null;
        const failedToken = !isLoading && payload && typeof payload === "object" && "failedAccessToken" in payload &&
            typeof payload.failedAccessToken === "string" && payload.failedAccessToken
            ? payload.failedAccessToken
            : null;
        entry.active = true;

        let current = this.deps.readToken();
        if (current && current.accessToken !== failedToken && this.hasEnoughValidity(current)) {
            this.postToken(entry, current);
            if (isLoading && this.holdsToken(entry, current)) entry.registration.onCompletedLoading?.();
            return;
        }

        const refreshed = await this.deps.refreshAccessToken();
        if (!this.entries.includes(entry)) return;

        current = this.deps.readToken();
        // Session gone: onSignedOut already broadcast AUTH_LOGOUT (or there never was a token).
        if (!current) return;

        const exp = expiresAt(current);
        const usable = refreshed !== null ||
            (current.accessToken !== failedToken && (isNaN(exp) || exp > this.now()));
        if (!usable) {
            this.post(entry, { type: EMBEDDED_APP_MESSAGE.ACCESS_TOKEN_ERROR, payload: { reason: "refresh-failed" } });
            console.warn("[EmbeddedAppTokenBroker] Token refresh failed");
            return;
        }

        // After a successful refresh the STATE_CHANGED push has usually delivered it already.
        if (refreshed === null || entry.lastSentToken !== current.accessToken) this.postToken(entry, current);
        if (isLoading && this.holdsToken(entry, current)) entry.registration.onCompletedLoading?.();
    }

    onTokenRefreshed(): void {
        const token = this.deps.readToken();
        if (!token) return;
        this.entries
            .filter(e => e.active && e.lastSentToken !== token.accessToken)
            .forEach(e => this.postToken(e, token));
    }

    onSignedOut(reason: SignOutReason): void {
        this.entries.filter(e => e.active).forEach(e => {
            this.post(e, { type: EMBEDDED_APP_MESSAGE.AUTH_LOGOUT, payload: { reason } });
            e.lastSentToken = null;
        });
    }

    private hasEnoughValidity(token: TokenSnapshot): boolean {
        const exp = expiresAt(token);
        return isNaN(exp) || exp - this.now() > MIN_TOKEN_VALIDITY_MS;
    }

    // The iframe can be gone by the time a refresh settles: onCompletedLoading runs only after
    // the ACCESS_TOKEN really reached a live window.
    private holdsToken(entry: Entry, token: TokenSnapshot): boolean {
        return entry.lastSentToken === token.accessToken && !!entry.registration.getContentWindow();
    }

    private postToken(entry: Entry, token: TokenSnapshot) {
        const payload: AccessTokenPayload = { ...token, languageId: entry.registration.getLanguageId() };
        if (this.post(entry, { type: EMBEDDED_APP_MESSAGE.ACCESS_TOKEN, payload })) {
            entry.lastSentToken = token.accessToken;
        }
    }

    private post(entry: Entry, message: { type: string, payload: object }): boolean {
        const win = entry.registration.getContentWindow();
        if (!win) return false;
        win.postMessage(message, entry.registration.origin);
        return true;
    }
}
